<template>
    <p-secure require-admin>
        <p-search-screen ref="searchScreen"
                         title="Users"
                         api-destination="users"
                         :filters="filters"
                         :filterBy="filterBy"
                         :fields="fields"
                         add-new-button
                         edit-key="userId"
                         @isActiveChanged="updateIsActiveStatus">
            <template v-slot:belowSearch>
                <div class="d-flex align-items-center gap-3 w-100">
                    <p-checkbox
                        label="Active"
                        v-model="filters.active"
                    />
                    <p-checkbox
                        label="Inactive"
                        v-model="filters.inactive"
                    />
                    <div class="d-flex align-items-center gap-3">
                        <p-checkbox
                            label="Enable 2FA for all users"
                            :use-switch="true"
                            v-model="tenantTwoFactorEnabled"
                            @change="toggleTenantTwoFactorEnabled"
                        />
                        <p-modal hide-footer
                                 name="ssoModal"
                                 ref="ssoModal"
                                 label="Configure SSO"
                                 :button-label="`Configure SSO`">
                            <template v-slot:default>
                                <p-form @submit='saveTenantIdProvider'>
                                    <p-card>
                                        <b-row>
                                            <b-col cols=12>
                                                <p-select label="Login Provider"
                                                          :options="ssoProviders"
                                                          v-model='selectedProvider'
                                                          mode="eager"
                                                          rules='required' />
                                                <p-input label='Scheme'
                                                         v-model='currentProvider.scheme'
                                                         rules='required|max:64' />
                                                <p-input label='Domain'
                                                         v-model='currentProvider.domain'
                                                         rules='required|max:64' />
                                                <p-input v-if="selectedProvider === 'Entra'"
                                                         label='Entra Tenant ID'
                                                         v-model='currentProvider.providerTenantId'
                                                         rules='required|max:64' />
                                                <p-input label='Client ID'
                                                         v-model='currentProvider.clientId'
                                                         rules='required|max:64' />
                                                <p-input label='Client Secret'
                                                         v-model='currentProvider.clientSecret'
                                                         rules='required|max:64'
                                                         autocomplete="off" />
                                            </b-col>
                                        </b-row>
                                    </p-card>
                                    <b-row class="mt-2">
                                        <b-col v-if="currentProvider.tenantIdentityProviderId">
                                            <b-button variant="danger" @click="deleteTenantIdProvider">Delete</b-button>
                                        </b-col>
                                        <b-col class="text-end">
                                            <b-button variant="primary" type="submit">Save</b-button>
                                        </b-col>
                                    </b-row>
                                </p-form>
                            </template>
                        </p-modal>
                    </div>
                </div>
            </template>
            <template #cell(twoFactorEnabled)="data">
                <b-icon-check v-if="data.item.twoFactorEnabled || tenantTwoFactorEnabled" scale="1.5" class="p-2" variant="success"></b-icon-check>
                <b-icon-x v-else scale="1.5" class="p-2" variant="danger"></b-icon-x>
            </template>
            <template #cell(userLogins)="data">
                <span v-for="(login, index) in data.item.userLogins" :key="index">{{login.loginProvider}}</span>
            </template>
        </p-search-screen>
    </p-secure>
</template>

<script>
import axios from 'axios';
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';

export default {
    mixins: [SearchStorage],
    data() {
        return {
            tenantTwoFactorEnabled: false,
            tenantIdProviders: [],
            selectedProvider: null,
            currentProvider: { loginProvider: null, scheme: null, domain: null, providerTenantId: null, clientId: null, clientSecret: null },
            ssoProviders: [
                { value: 'Okta', text: 'Okta' },
                { value: 'Entra', text: 'Entra' }
            ],
            filterName: 'UsersFilter',
            filters: {
                automatic: "",
                perPage: 100,
                sortBy: 'displayName',
                sortDesc: true,
                searchBy:'All',
                active: true,
                inactive: false,
                returnOnlyActiveRecords: true
            },
            filterBy:[
                'All',
                'Display Name',
                'Username',
                'Email'
            ],
            fields: [
                {
                    key: 'displayName',
                    sortable: true
                },
                {
                    key: 'userName',
                    sortable: true
                },
                {
                    key: 'email',
                    sortable: true
                },
                {
                    key: 'isActive',
                    sortable: false
                },
                {
                    key: 'twoFactorEnabled',
                    label: '2FA Enabled',
                    sortable: true
                },
                {
                    key: 'userLogins',
                    label: 'SSO Provider'
                }
            ]
        };
    },
    mounted: async function () {
        let result = await axios.get('utility/tenantTwoFactorEnabled');
        this.tenantTwoFactorEnabled = result.data;

        result = await axios.get('utility/tenantIdProviders');
        if (result.data) {
            this.tenantIdProviders = result.data;
        }
    },
    watch: {
        selectedProvider(providerName) {
            if (providerName) {
                this.selectProvider(providerName);
            }
        },
        'filters.active': 'syncActiveFilter',
        'filters.inactive': 'syncActiveFilter'
    },
    methods: {
        async toggleTenantTwoFactorEnabled(checked) {
            const msg = `You are about to ${ checked ?'activate': 'de-activate' } 2FA for ALL users. Are you sure this is what you want to do?`;
            const confirm = await this.$bvModal.msgBoxConfirm(msg);
                
            if (confirm) {
                const result = await axios.post('utility/toggleTenantTwoFactorEnabled', { twoFactorEnabled: checked });

                if (result) {
                    this.$appSuccess(`2FA for all users ${checked ? 'activated' : 'de-activated'
                        } successfully`);
                }
            } else {
                //revert user action
                this.tenantTwoFactorEnabled = !checked;
            }
        },
        selectProvider(providerName) {
            const existing = this.tenantIdProviders.find(p => p.loginProvider === providerName);
            if (existing) {
                this.currentProvider = { ...existing };
            } else {
                this.currentProvider = { loginProvider: providerName, scheme: null, domain: null, providerTenantId: null, clientId: null, clientSecret: null };
            }
        },
        async deleteTenantIdProvider() {
            const msg = `You are about to delete SSO provider ${this.currentProvider.loginProvider}. Are you sure this is what you want to do?`;
            const confirm = await this.$bvModal.msgBoxConfirm(msg);

            if (confirm) {
                await axios.post('utility/deleteTenantIdProvider', { tenantIdentityProviderId: this.currentProvider.tenantIdentityProviderId });
                this.$appSuccess('SSO Provider deleted');
                await this.refreshProviders();
                this.selectProvider(this.selectedProvider);
            }
        },
        async saveTenantIdProvider() {
            await axios.post('utility/saveTenantIdProvider', this.currentProvider);

            this.$appSuccess(
                    `SSO Provider '${this.currentProvider.loginProvider}' saved successfully.`
                )

            await this.refreshProviders();
            this.selectProvider(this.selectedProvider);
        },
        async refreshProviders() {
            const result = await axios.get('utility/tenantIdProviders');
            if (result.data) {
                this.tenantIdProviders = result.data;
            }
        },
        updateIsActiveStatus(user) {
            var self = this;
            axios.post('users', user).then(() => {
                self.$refs.searchScreen.refreshSearchResults();
                var msg = `${user.userName} ${
                    user.isActive ? 'activated' : 'de-activated'
                } successfully`;
                this.$appSuccess(msg);
            });
        },
        // Map the two active/inactive checkboxes onto the single
        // `returnOnlyActiveRecords` backend filter so the API doesn't change.
        syncActiveFilter() {
            if (this.filters.active && this.filters.inactive) {
                this.filters.returnOnlyActiveRecords = null;
            } else if (this.filters.active) {
                this.filters.returnOnlyActiveRecords = true;
            } else if (this.filters.inactive) {
                this.filters.returnOnlyActiveRecords = false;
            } else {
                this.filters.returnOnlyActiveRecords = null;
            }
        }
    },
};
</script>
<style scoped lang="scss">
:deep(.custom-select) {
    height: 43px;
}
</style>
